/**
 * Dock arrangement: the built-in panel list, the default layout and its
 * persistence. Kept free of React so it can be driven directly in tests.
 */
import type { DockviewApi, SerializedDockview } from 'dockview';

export interface PanelSpec {
  id: string;
  title: string;
}

export const BUILTIN_PANELS: readonly PanelSpec[] = [
  { id: 'preview', title: 'Preview' },
  { id: 'scenes', title: 'Scenes' },
  { id: 'sources', title: 'Sources' },
  { id: 'mixer', title: 'Audio Mixer' },
  { id: 'transitions', title: 'Scene Transitions' },
  { id: 'controls', title: 'Controls' },
  { id: 'stats', title: 'Stats' },
  { id: 'health', title: 'Stream Health' },
  { id: 'stream-manager', title: 'Stream Manager' },
  // Not in the default layout: it opens when the portal sends the creator an ad offer.
  { id: 'ad-offers', title: 'Ad Offers' },
];

export const PLUGIN_PANEL_PREFIX = 'plugin:';
export const LAYOUT_KEY = 'ohmyobs.ui.layout.v1';
const SEEN_DOCKS_KEY = 'ohmyobs.ui.seen-docks.v1';

/** Preview on top (Stream Manager tabbed behind it), the working docks in a row underneath, Stats tabbed behind the mixer. */
export function applyDefaultLayout(api: DockviewApi): void {
  api.clear();
  const add = (id: string, extra: Record<string, unknown> = {}): void => {
    const spec = BUILTIN_PANELS.find((p) => p.id === id)!;
    api.addPanel({ id, title: spec.title, component: id, ...extra });
  };
  add('preview');
  add('scenes', { position: { referencePanel: 'preview', direction: 'below' }, initialHeight: 250 });
  add('sources', { position: { referencePanel: 'scenes', direction: 'right' } });
  add('mixer', { position: { referencePanel: 'sources', direction: 'right' } });
  add('transitions', { position: { referencePanel: 'mixer', direction: 'right' }, initialWidth: 230 });
  add('controls', { position: { referencePanel: 'transitions', direction: 'right' }, initialWidth: 210 });
  add('stats', { position: { referencePanel: 'mixer', direction: 'within' }, inactive: true });
  add('health', { position: { referencePanel: 'mixer', direction: 'within' }, inactive: true });
  add('stream-manager', { position: { referencePanel: 'preview', direction: 'within' }, inactive: true });
  // Each split halves its reference group, so the row is sized once it exists: left to right, the
  // last dock taking what remains.
  if (api.width > 0) {
    const narrow = Math.max(170, Math.min(250, Math.round(api.width * 0.15)));
    const rest = Math.max(0, api.width - narrow * 2);
    const widths: [string, number][] = [
      ['scenes', Math.round(rest * 0.27)],
      ['sources', Math.round(rest * 0.4)],
      ['mixer', Math.round(rest * 0.33)],
      ['transitions', narrow],
    ];
    for (const [id, width] of widths) api.getPanel(id)?.group.api.setSize({ width });
  }
}

/** Opens a panel that is not in the layout, next to something sensible. */
export function openPanel(api: DockviewApi, spec: PanelSpec, component: string): void {
  if (api.getPanel(spec.id)) return;
  const anchor = ['mixer', 'sources', 'scenes', 'controls', 'preview'].find((id) => id !== spec.id && api.getPanel(id));
  if (spec.id === 'preview' && anchor) {
    api.addPanel({ id: spec.id, title: spec.title, component, position: { referencePanel: anchor, direction: 'above' } });
  } else if (anchor) {
    api.addPanel({ id: spec.id, title: spec.title, component, position: { referencePanel: anchor, direction: 'within' } });
  } else {
    api.addPanel({ id: spec.id, title: spec.title, component });
  }
}

type KeyValue = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function readSavedLayout(storage: KeyValue | null): SerializedDockview | null {
  try {
    const raw = storage?.getItem(LAYOUT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !('grid' in parsed) || !('panels' in parsed)) return null;
    return parsed as SerializedDockview;
  } catch {
    return null;
  }
}

export function saveLayout(storage: KeyValue | null, layout: SerializedDockview): void {
  try {
    storage?.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // Quota or privacy mode: the layout then lasts for this session only.
  }
}

export function clearSavedLayout(storage: KeyValue | null): void {
  try {
    storage?.removeItem(LAYOUT_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/**
 * Plugin docks open automatically the first time they appear. Remembering
 * which ones have been seen keeps a dock the user closed from reopening on
 * every start.
 */
export function readSeenDocks(storage: KeyValue | null): Set<string> {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(SEEN_DOCKS_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeSeenDocks(storage: KeyValue | null, seen: ReadonlySet<string>): void {
  try {
    storage?.setItem(SEEN_DOCKS_KEY, JSON.stringify([...seen]));
  } catch {
    // See saveLayout.
  }
}

export function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
